import * as React from "react"
import { Head } from "@inertiajs/react"
import { Pause, Play, ScrollText } from "lucide-react"
import { Button } from "@ui/Button"
import { EmptyState } from "@ui/EmptyState"
import { apiFetch } from "@lib/api"
import { cn } from "@lib/cn"
import { MethodBadge } from "@components/workspace/MethodBadge"
import type { MockRequestLog, Project } from "@lib/inertia-types"

export default function MocksLogs({
  project,
  logs: initialLogs,
}: {
  project: Project
  logs: MockRequestLog[]
}) {
  const [logs, setLogs] = React.useState<MockRequestLog[]>(initialLogs)
  const [live, setLive] = React.useState(true)
  const [expanded, setExpanded] = React.useState<number | null>(null)
  const maxId = React.useRef<number>(initialLogs[0]?.id ?? 0)

  React.useEffect(() => {
    if (!live) return
    const interval = setInterval(async () => {
      try {
        const fresh = await apiFetch<MockRequestLog[]>(
          `/projects/${project.slug}/mock_logs?since_id=${maxId.current}`,
        )
        if (fresh.length > 0) {
          maxId.current = Math.max(maxId.current, ...fresh.map((l) => l.id))
          setLogs((prev) => [...fresh, ...prev].slice(0, 300))
        }
      } catch {
        // transient; keep polling
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [live, project.slug])

  return (
    <>
      <Head title={`Mock logs · ${project.name} · Fetch`} />
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-cloverhound-gray-200 px-6 py-3 dark:border-cloverhound-gray-850">
          <div>
            <h1 className="text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
              Mock request log
            </h1>
            <p className="text-mini text-cloverhound-gray-600">
              Every hit on this project's mock endpoints, newest first.
            </p>
          </div>
          <Button variant={live ? "secondary" : "primary"} size="sm" onClick={() => setLive((v) => !v)}>
            {live ? <Pause className="size-3.5" aria-hidden="true" /> : <Play className="size-3.5" aria-hidden="true" />}
            {live ? "Live" : "Paused"}
          </Button>
        </div>

        {logs.length === 0 ? (
          <div className="grid flex-1 place-items-center">
            <EmptyState
              icon={<ScrollText className="size-6" aria-hidden="true" />}
              title="No mock hits yet"
              description="Requests to your mock URLs will stream in here."
            />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full text-mini">
              <thead className="sticky top-0 bg-white text-left text-micro uppercase tracking-wide text-cloverhound-gray-500 dark:bg-cloverhound-gray-950">
                <tr className="border-b border-cloverhound-gray-200 dark:border-cloverhound-gray-850">
                  <th className="px-4 py-2 font-medium">Time</th>
                  <th className="px-2 py-2 font-medium">Method</th>
                  <th className="px-2 py-2 font-medium">Path</th>
                  <th className="px-2 py-2 font-medium">Status</th>
                  <th className="px-2 py-2 font-medium">Match</th>
                  <th className="px-2 py-2 font-medium">ms</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr
                      onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                      className="cursor-pointer border-b border-cloverhound-gray-150 hover:bg-cloverhound-gray-50 dark:border-cloverhound-gray-850 dark:hover:bg-cloverhound-gray-900"
                    >
                      <td className="whitespace-nowrap px-4 py-1.5 font-mono text-cloverhound-gray-600">
                        {new Date(log.created_at).toLocaleTimeString()}
                      </td>
                      <td className="px-2 py-1.5">
                        <MethodBadge method={log.http_method} />
                      </td>
                      <td className="px-2 py-1.5 font-mono text-cloverhound-gray-900 dark:text-cloverhound-gray-100">
                        {log.path}
                        {log.query_string && <span className="text-cloverhound-gray-500">?{log.query_string}</span>}
                      </td>
                      <td className="px-2 py-1.5 font-mono">{log.response_status}</td>
                      <td className="px-2 py-1.5">
                        <span
                          className={cn(
                            "rounded-pill px-1.5 py-0.5 text-micro font-medium",
                            log.matched
                              ? "bg-cloverhound-green-50 text-cloverhound-green-700 dark:bg-cloverhound-green-500/10 dark:text-cloverhound-green-400"
                              : "bg-cloverhound-red-50 text-cloverhound-red-600 dark:bg-cloverhound-red-500/10 dark:text-cloverhound-red-400",
                          )}
                        >
                          {log.matched ? "matched" : "no match"}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 font-mono text-cloverhound-gray-500">{log.duration_ms}</td>
                    </tr>
                    {expanded === log.id && (
                      <tr className="bg-cloverhound-gray-50 dark:bg-cloverhound-gray-900">
                        <td colSpan={6} className="px-4 py-3">
                          <LogDetail log={log} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

function LogDetail({ log }: { log: MockRequestLog }) {
  return (
    <div className="grid grid-cols-2 gap-4 font-mono text-micro">
      <div>
        <p className="mb-1 font-semibold text-cloverhound-gray-700 dark:text-cloverhound-gray-300">Request headers</p>
        <pre className="whitespace-pre-wrap break-all text-cloverhound-gray-600">
          {log.request_headers_raw || JSON.stringify(log.request_header_lines ?? log.request_headers ?? {}, null, 2)}
        </pre>
        {log.http_version && <p className="mt-1 text-cloverhound-gray-500">{log.http_version}</p>}
        {log.request_body && (
          <>
            <p className="mb-1 mt-2 font-semibold text-cloverhound-gray-700 dark:text-cloverhound-gray-300">Request body</p>
            <pre className="whitespace-pre-wrap break-all text-cloverhound-gray-600">{log.request_body}</pre>
          </>
        )}
      </div>
      <div>
        <p className="mb-1 font-semibold text-cloverhound-gray-700 dark:text-cloverhound-gray-300">Response body</p>
        <pre className="whitespace-pre-wrap break-all text-cloverhound-gray-600">{log.response_body ?? "(empty)"}</pre>
        <p className="mt-2 text-cloverhound-gray-500">IP {log.ip_address}</p>
      </div>
    </div>
  )
}
